import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, User, Check, MessageCircle, GraduationCap, ChevronDown } from "lucide-react";
import { ScrollReveal } from "./Reveal";
import { scrollToId } from "./Navbar";

const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3 text-sm sm:text-base text-charcoal/80">
    <Check size={18} className="text-hanko mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);

export default function Cursuri() {
  const [bursaOpen, setBursaOpen] = useState(false);

  return (
    <section id="cursuri" data-testid="cursuri-section" className="relative py-20 sm:py-32 bg-washiaccent/50 border-y border-sumi/10 overflow-hidden">
      <span aria-hidden className="pointer-events-none select-none absolute right-0 top-10 vertical-rl font-jp text-8xl text-sumi/[0.05] hidden lg:block">
        授業と料金
      </span>

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <ScrollReveal>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-hanko mb-4">Cursuri & Prețuri</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-sumi leading-tight max-w-3xl">
            Un modul. <span className="italic text-hanko">Un drum</span> mai departe.
          </h2>
          <p className="mt-5 max-w-2xl text-charcoal/80 leading-relaxed">
            Curriculum aliniat JLPT, cu manualul Genki.
            Plata integrală sau în două rate egale. Fără rambursări — simplu și transparent.
          </p>
        </ScrollReveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          <ScrollReveal delay={0}>
            <article
              data-testid="pricing-card-group"
              className="group h-full bg-white border border-sumi/10 p-8 flex flex-col transition-all duration-500 hover:-translate-y-2 hover:border-hanko/50 hover:shadow-[0_24px_60px_-24px_rgba(211,47,47,0.25)]"
            >
              <div className="flex items-center justify-between">
                <Users size={26} className="text-hanko" />
                <span className="text-[11px] font-mono tracking-widest uppercase text-charcoal/50">6–10 elevi</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-sumi">Grupă</h3>
              <p className="text-xs font-mono tracking-widest uppercase text-charcoal/50 mt-1">Începător · Intermediar · Avansat</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-serif text-5xl text-sumi">1800</span>
                <span className="text-charcoal/60 text-sm">lei / modul</span>
              </div>
              <ul className="mt-7 space-y-3.5 flex-1">
                <CheckItem>Online sau față în față</CheckItem>
                <CheckItem>Ședințe de 1h30</CheckItem>
                <CheckItem>Minimum 14 lecții pe modul</CheckItem>
                <CheckItem>Plată integrală sau 2 rate egale</CheckItem>
                <CheckItem>Conversația de vineri: +200 lei / modul</CheckItem>
              </ul>
              <button
                data-testid="pricing-select-group-button"
                onClick={() => scrollToId("#lectie")}
                className="mt-8 border border-sumi/25 text-sumi font-semibold py-3.5 rounded-full group-hover:bg-hanko group-hover:border-hanko group-hover:text-washi transition-colors duration-300"
              >
                Începe cu o lecție gratuită
              </button>
            </article>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <article
              data-testid="pricing-card-individual"
              className="group h-full bg-sumi text-washi p-8 flex flex-col transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_60px_-20px_rgba(18,18,18,0.5)] relative overflow-hidden"
            >
              <span aria-hidden className="absolute -right-4 -bottom-8 font-jp text-[9rem] leading-none text-washi/[0.06] select-none">一</span>
              <div className="flex items-center justify-between relative">
                <User size={26} className="text-hanko" />
                <span className="text-[11px] font-mono tracking-widest uppercase text-washi/50">1 la 1</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl relative">Lecții individuale</h3>
              <p className="text-xs font-mono tracking-widest uppercase text-washi/50 mt-1 relative">Ritmul tău, programul tău</p>
              <div className="mt-6 flex items-baseline gap-2 relative">
                <span className="font-serif text-5xl">2700</span>
                <span className="text-washi/60 text-sm">lei / modul</span>
              </div>
              <ul className="mt-7 space-y-3.5 flex-1 relative [&_li]:text-washi/85">
                <CheckItem>Atenție integrală a profesorului</CheckItem>
                <CheckItem>Program flexibil, online sau față în față</CheckItem>
                <CheckItem>Ședințe de 1h30</CheckItem>
                <CheckItem>Plată integrală sau 2 rate egale</CheckItem>
                <CheckItem>Conversația de vineri: gratuită</CheckItem>
              </ul>
              <button
                data-testid="pricing-select-individual-button"
                onClick={() => scrollToId("#lectie")}
                className="mt-8 bg-hanko text-washi font-semibold py-3.5 rounded-full hover:bg-hankodeep transition-colors duration-300 relative"
              >
                Începe cu o lecție gratuită
              </button>
            </article>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.1}>
          <div
            data-testid="friday-conversation-strip"
            className="mt-10 border border-sumi/15 bg-white px-6 sm:px-10 py-7 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <div className="flex items-center gap-4 shrink-0">
              <span className="w-12 h-12 rounded-full bg-hanko/10 flex items-center justify-center">
                <MessageCircle size={22} className="text-hanko" />
              </span>
              <h4 className="font-serif text-xl text-sumi">Extra · vineri seara</h4>
            </div>
            <p className="text-sm sm:text-base text-charcoal/75 leading-relaxed sm:ml-auto max-w-2xl">
              O oră în fiecare vineri seară — și nu e doar conversație: învățăm despre istorie,
              tradiții și obiceiuri japoneze.
              <span className="font-semibold text-sumi"> Gratuit</span> pentru elevii cu lecții individuale,
              <span className="font-semibold text-sumi"> +200 lei / modul</span> pentru cei la grupă.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.12}>
          <div data-testid="bursa-section" className="mt-6 border border-sumi/15 bg-white px-6 sm:px-10 py-7">
            <div className="flex flex-col sm:flex-row sm:items-start gap-5">
              <div className="flex items-center gap-4 shrink-0">
                <span className="w-12 h-12 rounded-full bg-hanko/10 flex items-center justify-center">
                  <GraduationCap size={22} className="text-hanko" />
                </span>
                <h4 className="font-serif text-xl text-sumi">Bursa de merit</h4>
              </div>
              <div className="sm:ml-auto max-w-2xl">
                <p className="text-sm sm:text-base text-charcoal/75 leading-relaxed">
                  <span className="font-semibold text-sumi">Bursa de merit SORA — 20% din costul modulului.</span>{" "}
                  La SORA credem că talentul și seriozitatea nu ar trebui limitate de posibilitățile materiale:
                  oferim o bursă de 20% elevilor cu media generală peste 9,50, aflați într-o situație financiară dificilă.
                  Locurile sunt limitate.
                </p>
                <button
                  data-testid="bursa-more-info-button"
                  onClick={() => setBursaOpen(!bursaOpen)}
                  aria-expanded={bursaOpen}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-hanko hover:text-hankodeep transition-colors duration-300 min-h-[44px]"
                >
                  {bursaOpen ? "Mai puține informații" : "Mai multe informații"}
                  <ChevronDown size={16} className={`transition-transform duration-300 ${bursaOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {bursaOpen && (
                <motion.div
                  data-testid="bursa-details"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pt-6 mt-6 border-t border-sumi/10 space-y-6 text-sm sm:text-base text-charcoal/75 leading-relaxed max-w-3xl">
                    <p>
                      La Sora Japanese School credem că talentul și seriozitatea nu ar trebui să fie limitate de
                      posibilitățile materiale. De aceea, oferim o bursă de 20% elevilor care au obținut o medie
                      generală de peste 9,50 și care se află într-o situație financiară dificilă.
                    </p>
                    <div>
                      <p className="font-semibold text-sumi mb-2">Cine poate aplica:</p>
                      <ul className="space-y-1.5">
                        <li className="flex gap-3"><span className="text-hanko">·</span> Elevii cu media generală peste 9,50 în ultimul an școlar încheiat.</li>
                        <li className="flex gap-3"><span className="text-hanko">·</span> Elevii a căror situație financiară face dificilă plata integrală a cursurilor.</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-sumi mb-2">Cum aplici:</p>
                      <p>
                        Trimite la{" "}
                        <a href="mailto:Sorajapaneseschool48@gmail.com" className="text-hanko font-medium hover:underline" data-testid="bursa-email-link">
                          Sorajapaneseschool48@gmail.com
                        </a>{" "}
                        adeverința cu media generală, o declarație privind situația financiară și o scurtă scrisoare
                        de motivație. Îți vom răspunde prin e-mail cu rezultatul.
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-sumi mb-2">Condiții:</p>
                      <ul className="space-y-1.5">
                        <li className="flex gap-3"><span className="text-hanko">·</span> Bursa se acordă pentru un modul și poate fi reînnoită în funcție de prezență și implicare.</li>
                        <li className="flex gap-3"><span className="text-hanko">·</span> Bursa nu se aplică orelor suplimentare de vineri și nici manualelor.</li>
                        <li className="flex gap-3"><span className="text-hanko">·</span> Locurile sunt limitate.</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.15}>
          <p className="mt-8 text-xs font-mono tracking-wider uppercase text-charcoal/50 text-center" data-testid="pricing-terms-note">
            Plată integrală sau în două rate egale · Fără rambursări
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
